import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PdfUploader from "@/components/PdfUploader";
import api, { sessionAPI, userAPI } from "@/lib/api";
import {
  Mic, Loader2, CheckCircle, ArrowRight, ArrowLeft, BarChart3,
  Target, Shield, Brain, FileText, MessageSquare, AlertTriangle, Lightbulb,
  Upload, X, ClipboardList, Download, Share2, Lock, Crown
} from "lucide-react";
import { toast } from "sonner";

const PROFESSIONS = [
  "Doctor", "Nurse", "Physiotherapist", "Occupational Therapist",
  "Speech & Language Therapist", "Radiographer", "Pharmacist",
  "Paramedic", "Biomedical Scientist", "Dietitian", "Podiatrist", "Psychologist", "Other"
];
const BANDS = ["Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b", "Band 8c", "Band 8d", "Band 9"];

const TYPE_BADGES = {
  clinical: { label: "Clinical", className: "bg-[#D5281B]/10 text-[#D5281B]" },
  competency: { label: "Competency", className: "bg-[#003087]/10 text-[#003087]" },
  leadership: { label: "Leadership", className: "bg-[#00A3A6]/10 text-[#00A3A6]" },
  governance: { label: "Governance", className: "bg-[#330072]/10 text-[#330072]" },
  operational: { label: "Operational", className: "bg-[#FFB81C]/10 text-[#9A6700]" },
  scenario: { label: "Scenario", className: "bg-[#007F3B]/10 text-[#007F3B]" },
  values: { label: "Values", className: "bg-[#003087]/10 text-[#003087]" },
};

export default function InterviewSimulator() {
  const { user } = useAuth();
  const [step, setStep] = useState("setup");
  const [profession, setProfession] = useState("");
  const [band, setBand] = useState("");
  const [loading, setLoading] = useState(false);

  const [jdText, setJdText] = useState("");
  const [jdFileName, setJdFileName] = useState("");
  const [psText, setPsText] = useState("");
  const [psFileName, setPsFileName] = useState("");
  const [cvText, setCvText] = useState("");
  const [cvFileName, setCvFileName] = useState("");

  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [guidance, setGuidance] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [evaluation, setEvaluation] = useState(null);

  // Usage state
  const [usageInfo, setUsageInfo] = useState(null);

  const fetchUsage = useCallback(() => {
    userAPI.getUsage().then(r => setUsageInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const plan = usageInfo?.plan || user?.subscription_plan || "free";
  const interviewLimit = usageInfo?.plan_config?.interview_sim_limit ?? 0;
  const interviewUsed = usageInfo?.usage?.interview_sim ?? 0;
  const isPremium = plan === "premium";
  const limitReached = usageInfo !== null && !isPremium && interviewLimit >= 0 && interviewUsed >= interviewLimit;
  const resetDate = usageInfo?.period_end
    ? new Date(usageInfo.period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "";

  const canStart = profession && band && jdText && psText;

  const handleJdExtracted = (text, filename) => { setJdText(text); setJdFileName(filename); };
  const handlePsExtracted = (text, filename) => { setPsText(text); setPsFileName(filename); };
  const handleCvExtracted = (text, filename) => { setCvText(text); setCvFileName(filename); };
  const clearJd = () => { setJdText(""); setJdFileName(""); };
  const clearPs = () => { setPsText(""); setPsFileName(""); };
  const clearCv = () => { setCvText(""); setCvFileName(""); };

  const handleGenerate = async () => {
    if (limitReached) {
      toast.error("You've used your interview simulation for this period. Upgrade to Premium for unlimited simulations.");
      return;
    }
    if (!canStart) {
      toast.error("Please upload both Job Description and Person Specification");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/interview/generate", {
        profession, band,
        job_description: jdText,
        person_specification: psText,
        cv_text: cvText || null,
      });
      setSessionId(res.data.session_id);
      setQuestions(res.data.questions || []);
      setGuidance(res.data.interview_guidance || "");
      setAnalysis({
        detectedBand: res.data.detected_band,
        missingSkills: res.data.missing_skills || [],
        strengths: res.data.strengths || [],
        recommendations: res.data.recommendations || [],
        cvImprovements: res.data.cv_improvements || [],
        criteriaCoverage: res.data.criteria_coverage || {},
      });
      setAnswers({});
      setCurrentQ(0);
      setStep("questions");
      toast.success(`Generated ${res.data.questions?.length || 0} interview questions!`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.limit_reached) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to generate questions");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const answerList = questions.map((_, i) => answers[i] || "");
    const unanswered = answerList.filter(a => !a.trim()).length;
    if (unanswered > 3) {
      toast.error(`Please answer at least ${questions.length - 3} questions`);
      return;
    }
    setStep("submitting");
    setLoading(true);
    try {
      const res = await api.post("/interview/evaluate", {
        session_id: sessionId,
        questions,
        answers: answerList,
      });
      setEvaluation(res.data);
      setStep("results");
      fetchUsage();
      toast.success("Evaluation complete!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Evaluation failed");
      setStep("questions");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    const currentSession = sessionId;
    try {
      if (currentSession) await sessionAPI.reset({ session_id: currentSession });
      else await sessionAPI.reset({});
    } catch { /* ignore */ }
    setStep("setup");
    setJdText(""); setJdFileName("");
    setPsText(""); setPsFileName("");
    setCvText(""); setCvFileName("");
    setSessionId(null); setQuestions([]); setGuidance(""); setAnalysis(null);
    setAnswers({}); setCurrentQ(0); setEvaluation(null);
  };

  // --- Share & Download ---
  const handleDownloadPDF = async () => {
    if (!evaluation) return;
    try {
      const res = await api.post("/interview/download-pdf", {
        overall_score: evaluation.overall_score,
        overall_assessment: evaluation.overall_assessment,
        summary: evaluation.summary || "",
        key_strengths: evaluation.key_strengths || [],
        development_areas: evaluation.development_areas || [],
        preparation_recommendations: evaluation.preparation_recommendations || [],
        band_readiness: evaluation.band_readiness || "",
        answer_feedback: (evaluation.answer_feedback || []).map(fb => ({
          question_id: fb.question_id || 0,
          score: fb.score || 0,
          assessment: fb.assessment || "",
          question_type: fb.question_type || "",
          panel_competency_being_assessed: fb.panel_competency_being_assessed || "",
          panel_expectations_at_band: fb.panel_expectations_at_band || "",
          what_was_done_well: fb.what_was_done_well || "",
          areas_to_strengthen: fb.areas_to_strengthen || "",
          improvement_guidance: fb.improvement_guidance || "",
          example_improvement: fb.example_improvement || "",
        })),
        questions: questions,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `bandboost_interview_result_${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const handleShare = async () => {
    if (!evaluation) return;
    const shareText = `I just scored ${evaluation.overall_score}/100 on the BandBoost NHS Interview Simulator.\n\nPanel assessment: ${evaluation.overall_assessment}\n\nTry it here:\n${window.location.origin}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "BandBoost Interview Result", text: shareText });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success("Result copied to clipboard");
      } catch {
        toast.error("Could not copy to clipboard");
      }
    }
  };

  // --- Usage banner ---
  const UsageBanner = () => {
    if (isPremium) return null;
    if (limitReached) {
      return (
        <Card className="bg-amber-50 border border-amber-200 rounded-xl" data-testid="interview-limit-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                You've used your free interview simulation ({interviewUsed}/{interviewLimit}).
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Resets on: {resetDate}. Upgrade to Premium for unlimited interview simulations with detailed panel feedback, band-calibrated questions, and leadership & governance assessment.
              </p>
              <Button size="sm" className="mt-2 bg-[#003087] text-white hover:bg-[#002366]" onClick={() => window.location.href = "/pricing"} data-testid="interview-upgrade-btn">
                <Crown className="w-3.5 h-3.5 mr-1" /> Upgrade to Premium
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }
    if (interviewLimit > 0) {
      return (
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded-lg" data-testid="interview-usage-info">
          <BarChart3 className="w-3.5 h-3.5" />
          <span>Interview Simulation: {interviewUsed} / {interviewLimit} used</span>
          {resetDate && <span className="text-slate-400">| Resets on: {resetDate}</span>}
        </div>
      );
    }
    return null;
  };

  // --- Upgrade prompt on results page for Free/Pro ---
  const UpgradePrompt = () => {
    if (isPremium) return null;
    return (
      <Card className="bg-[#003087]/5 border border-[#003087]/15 rounded-xl" data-testid="interview-upgrade-prompt">
        <CardContent className="p-4 text-center">
          <p className="text-sm text-slate-700">
            Improve your performance with another simulation. Unlimited simulations are available on Premium.
          </p>
          <Button size="sm" className="mt-2 bg-[#003087] text-white hover:bg-[#002366]" onClick={() => window.location.href = "/pricing"}>
            <Crown className="w-3.5 h-3.5 mr-1" /> View Premium Plan
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="interview-simulator-page">
        <div>
          <h1 className="text-2xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>Interview Simulator</h1>
          <p className="text-slate-500 mt-1">Practice NHS interview questions calibrated to your target role.</p>
        </div>

        {/* Usage Banner */}
        {step === "setup" && <UsageBanner />}

        {/* Setup */}
        {step === "setup" && (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-xl animate-fade-in" data-testid="interview-setup">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: "Public Sans" }}>Start Interview Simulation</CardTitle>
              <CardDescription>
                Upload the Job Description and Person Specification for this application to generate band-calibrated, criteria-specific NHS interview questions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Profession *</Label>
                  <Select value={profession} onValueChange={setProfession}>
                    <SelectTrigger data-testid="interview-profession"><SelectValue placeholder="Select profession" /></SelectTrigger>
                    <SelectContent>{PROFESSIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Target Band *</Label>
                  <Select value={band} onValueChange={setBand}>
                    <SelectTrigger data-testid="interview-band"><SelectValue placeholder="Select band" /></SelectTrigger>
                    <SelectContent>{BANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 text-slate-700">
                  <ClipboardList className="w-5 h-5 text-[#003087]" />
                  <span className="font-medium">Required Documents</span>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700">Job Description *</Label>
                  <p className="text-xs text-slate-500">Upload the JD for this specific role to generate relevant questions</p>
                  {jdFileName ? (
                    <div className="flex items-center gap-2 p-3 bg-white rounded border border-slate-200">
                      <FileText className="w-4 h-4 text-[#007F3B]" />
                      <span className="text-sm text-slate-700 flex-1 truncate">{jdFileName}</span>
                      <CheckCircle className="w-4 h-4 text-[#007F3B]" />
                      <Button variant="ghost" size="sm" onClick={clearJd} className="h-6 w-6 p-0" data-testid="clear-jd-btn">
                        <X className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <PdfUploader onTextExtracted={handleJdExtracted} label="Upload JD (PDF or DOCX)" />
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700">Person Specification *</Label>
                  <p className="text-xs text-slate-500">Upload the PS to ensure questions cover all essential and desirable criteria</p>
                  {psFileName ? (
                    <div className="flex items-center gap-2 p-3 bg-white rounded border border-slate-200">
                      <FileText className="w-4 h-4 text-[#007F3B]" />
                      <span className="text-sm text-slate-700 flex-1 truncate">{psFileName}</span>
                      <CheckCircle className="w-4 h-4 text-[#007F3B]" />
                      <Button variant="ghost" size="sm" onClick={clearPs} className="h-6 w-6 p-0" data-testid="clear-ps-btn">
                        <X className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <PdfUploader onTextExtracted={handlePsExtracted} label="Upload PS (PDF or DOCX)" />
                  )}
                </div>

                <div className="space-y-2 pt-2 border-t border-slate-200">
                  <Label className="text-slate-700">Your CV or Supporting Information (Optional)</Label>
                  <p className="text-xs text-slate-500">Upload your CV or Supporting Information used for this application to receive personalised gap analysis</p>
                  {cvFileName ? (
                    <div className="flex items-center gap-2 p-3 bg-white rounded border border-slate-200">
                      <FileText className="w-4 h-4 text-[#00A3A6]" />
                      <span className="text-sm text-slate-700 flex-1 truncate">{cvFileName}</span>
                      <CheckCircle className="w-4 h-4 text-[#00A3A6]" />
                      <Button variant="ghost" size="sm" onClick={clearCv} className="h-6 w-6 p-0" data-testid="clear-cv-btn">
                        <X className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <PdfUploader onTextExtracted={handleCvExtracted} label="Upload CV (PDF or DOCX)" />
                  )}
                </div>
              </div>

              <div className="bg-[#003087]/5 p-4 rounded-lg text-sm text-slate-600 space-y-1">
                <p className="font-medium text-[#003087]">Your interview will include:</p>
                <p>10 questions derived from the JD and Person Specification</p>
                <p>Questions calibrated to {band || "your target band"} expectations</p>
                <p>Coverage of essential and desirable criteria</p>
                <p>Structured feedback with improvement guidance</p>
              </div>

              <Button
                className="w-full bg-[#003087] text-white hover:bg-[#002366] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleGenerate}
                disabled={loading || !canStart || limitReached}
                data-testid="interview-start-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Questions...</>
                ) : limitReached ? (
                  <><Lock className="w-4 h-4 mr-2" /> Simulation Limit Reached</>
                ) : (
                  <><Mic className="w-4 h-4 mr-2" /> Start Interview Simulation</>
                )}
              </Button>

              {!canStart && !limitReached && (
                <p className="text-xs text-center text-slate-500">
                  {!profession && "Select profession "}
                  {!band && "Select band "}
                  {!jdText && "Upload Job Description "}
                  {!psText && "Upload Person Specification"}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Questions */}
        {step === "questions" && questions.length > 0 && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span>Question {currentQ + 1} of {questions.length}</span>
              <span>{Object.values(answers).filter(a => a?.trim()).length} answered</span>
            </div>
            <Progress value={((currentQ + 1) / questions.length) * 100} className="h-2" />

            {currentQ === 0 && (guidance || analysis) && (
              <Card className="bg-[#003087]/5 border border-[#003087]/15 rounded-xl">
                <CardContent className="p-4 text-sm text-slate-700 space-y-3">
                  {analysis?.detectedBand && (
                    <p className="text-xs"><Badge variant="outline">{analysis.detectedBand} Level Interview</Badge></p>
                  )}
                  {guidance && (
                    <div>
                      <p className="font-medium text-[#003087] mb-1">Panel Guidance</p>
                      <p>{guidance}</p>
                    </div>
                  )}
                  {analysis?.criteriaCoverage?.essential_covered?.length > 0 && (
                    <div>
                      <p className="font-medium text-[#003087] mb-1">Criteria Being Assessed</p>
                      <p className="text-xs text-slate-600">
                        Essential: {analysis.criteriaCoverage.essential_covered.length} criteria | 
                        Desirable: {analysis.criteriaCoverage.desirable_covered?.length || 0} criteria
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid={`interview-q-${currentQ}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className={TYPE_BADGES[questions[currentQ]?.type]?.className || TYPE_BADGES.competency.className}>
                    {TYPE_BADGES[questions[currentQ]?.type]?.label || "Competency"}
                  </Badge>
                  {questions[currentQ]?.requirement_type && (
                    <Badge variant="outline" className={questions[currentQ].requirement_type === "essential" ? "border-[#D5281B] text-[#D5281B]" : "text-slate-500"}>
                      {questions[currentQ].requirement_type}
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-base text-slate-900">
                  Q{currentQ + 1}. {questions[currentQ]?.text}
                </CardTitle>
                {questions[currentQ]?.criterion && (
                  <p className="text-xs text-slate-500 mt-1">
                    Assessing: {questions[currentQ].criterion}
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {questions[currentQ]?.tips && (
                  <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded flex items-start gap-1.5">
                    <Lightbulb className="w-3.5 h-3.5 text-[#FFB81C] mt-0.5 flex-shrink-0" />
                    <span><strong>Tip:</strong> {questions[currentQ].tips}</span>
                  </p>
                )}
                <Textarea
                  placeholder={questions[currentQ]?.type === "clinical"
                    ? "Describe your clinical reasoning, patient safety considerations, and decision-making process..."
                    : "Structure your answer: Situation, Task, Action, Result..."}
                  className="min-h-[160px]"
                  value={answers[currentQ] || ""}
                  onChange={(e) => setAnswers(prev => ({ ...prev, [currentQ]: e.target.value }))}
                  data-testid="interview-answer-input"
                />
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0} data-testid="interview-prev-btn">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  {currentQ < questions.length - 1 ? (
                    <Button className="bg-[#003087] text-white hover:bg-[#002366]" onClick={() => setCurrentQ(currentQ + 1)} data-testid="interview-next-btn">
                      Next <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button className="bg-[#007F3B] text-white hover:bg-[#006330]" onClick={handleSubmit} data-testid="interview-submit-btn">
                      <CheckCircle className="w-4 h-4 mr-1" /> Submit for Evaluation
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-center gap-1.5 flex-wrap">
              {questions.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentQ(i)}
                  className={`w-7 h-7 rounded-full text-xs font-medium transition-colors ${
                    i === currentQ ? "bg-[#003087] text-white" :
                    answers[i]?.trim() ? "bg-[#007F3B] text-white" : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                  }`}
                  data-testid={`interview-dot-${i}`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Submitting */}
        {step === "submitting" && (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
            <CardContent className="p-10 text-center">
              <Loader2 className="w-10 h-10 text-[#003087] animate-spin mx-auto mb-4" />
              <p className="text-slate-600 font-medium">Evaluating your answers...</p>
              <p className="text-slate-400 text-sm mt-1">The panel is reviewing your responses against {band} expectations</p>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {step === "results" && evaluation && (
          <div className="space-y-4 animate-fade-in" data-testid="interview-results">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="interview-overall-score">
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div className="relative w-24 h-24 flex-shrink-0">
                    <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none"
                        stroke={evaluation.overall_score >= 70 ? "#007F3B" : evaluation.overall_score >= 40 ? "#FFB81C" : "#D5281B"}
                        strokeWidth="8"
                        strokeDasharray={`${(evaluation.overall_score / 100) * 264} 264`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-slate-900">
                      {evaluation.overall_score}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Public Sans" }}>Overall Score</h3>
                      <Badge className={
                        evaluation.overall_assessment === "Strong" ? "bg-[#007F3B]/10 text-[#007F3B]" :
                        evaluation.overall_assessment === "Moderate" ? "bg-[#FFB81C]/10 text-[#9A6700]" :
                        evaluation.overall_assessment === "No Response" ? "bg-slate-200 text-slate-600" :
                        "bg-[#D5281B]/10 text-[#D5281B]"
                      }>
                        {evaluation.overall_assessment}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600">{evaluation.summary}</p>
                    {evaluation.band_readiness && (
                      <p className="text-xs text-slate-500 mt-2 p-2 bg-slate-50 rounded">{evaluation.band_readiness}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Share & Download Actions */}
            <div className="flex gap-3 justify-center" data-testid="interview-share-actions">
              <Button variant="outline" onClick={handleDownloadPDF} data-testid="interview-download-pdf-btn">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
              <Button variant="outline" onClick={handleShare} data-testid="interview-share-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share Result
              </Button>
            </div>

            {/* Strengths & Development Areas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {evaluation.key_strengths?.length > 0 && (
                <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#007F3B]" /> Key Strengths
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {evaluation.key_strengths.map((s, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-[#007F3B] mt-0.5 flex-shrink-0" /> {s}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              {evaluation.development_areas?.length > 0 && (
                <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-[#FFB81C]" /> Areas to Develop
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {evaluation.development_areas.map((a, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-[#FFB81C] mt-0.5 flex-shrink-0" /> {a}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Per-answer feedback */}
            {evaluation.answer_feedback?.length > 0 && (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="interview-answer-feedback">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Detailed Answer Feedback</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {evaluation.answer_feedback.map((fb, i) => (
                    <details key={i} className="group">
                      <summary className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <span className="text-sm font-medium text-slate-900">Q{fb.question_id || i + 1}</span>
                        <Badge className={
                          fb.assessment === "Strong" ? "bg-[#007F3B]/10 text-[#007F3B]" :
                          fb.assessment === "Moderate" ? "bg-[#FFB81C]/10 text-[#9A6700]" :
                          fb.assessment === "No Response" ? "bg-slate-200 text-slate-600" :
                          "bg-[#D5281B]/10 text-[#D5281B]"
                        } variant="outline">
                          {fb.assessment}
                        </Badge>
                        <span className="text-xs text-slate-500 ml-auto">{fb.score}/10</span>
                      </summary>
                      <div className="p-3 text-sm space-y-3">
                        {fb.panel_competency_being_assessed && (
                          <div className="bg-[#003087]/5 p-2.5 rounded" data-testid={`feedback-competency-${i}`}>
                            <p className="text-xs font-semibold text-[#003087] mb-0.5">Panel Competency Being Assessed</p>
                            <p className="text-slate-700">{fb.panel_competency_being_assessed}</p>
                          </div>
                        )}
                        {fb.panel_expectations_at_band && (
                          <div className="bg-slate-50 p-2.5 rounded border border-slate-200" data-testid={`feedback-expectations-${i}`}>
                            <p className="text-xs font-semibold text-slate-600 mb-0.5">Panel Expectations at {band || "Band"}</p>
                            <p className="text-slate-600 text-xs leading-relaxed">{fb.panel_expectations_at_band}</p>
                          </div>
                        )}
                        {fb.what_was_done_well && (
                          <div>
                            <p className="text-xs font-medium text-[#007F3B] mb-1">What Was Done Well</p>
                            <p className="text-slate-600">{fb.what_was_done_well}</p>
                          </div>
                        )}
                        {fb.areas_to_strengthen && (
                          <div>
                            <p className="text-xs font-medium text-[#FFB81C] mb-1">Areas to Strengthen</p>
                            <p className="text-slate-600">{fb.areas_to_strengthen}</p>
                          </div>
                        )}
                        {fb.improvement_guidance && (
                          <div className="bg-[#003087]/5 p-2.5 rounded">
                            <p className="text-xs font-medium text-[#003087] mb-1">Improvement Guidance</p>
                            <p className="text-slate-600">{fb.improvement_guidance}</p>
                          </div>
                        )}
                        {fb.example_improvement && (
                          <div className="bg-[#007F3B]/5 p-2.5 rounded border-l-2 border-[#007F3B]" data-testid={`feedback-example-${i}`}>
                            <p className="text-xs font-medium text-[#007F3B] mb-1">Example Improvement</p>
                            <p className="text-slate-600 italic text-xs leading-relaxed">"{fb.example_improvement}"</p>
                          </div>
                        )}
                      </div>
                    </details>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Preparation Recommendations */}
            {evaluation.preparation_recommendations?.length > 0 && (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-[#FFB81C]" /> Preparation Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {evaluation.preparation_recommendations.map((r, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <ArrowRight className="w-3.5 h-3.5 text-[#003087] mt-0.5 flex-shrink-0" /> {r}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Upgrade prompt for Free/Pro */}
            <UpgradePrompt />

            {/* Actions */}
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={handleReset} data-testid="interview-new-btn">
                Start New Interview
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
